import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import ColorModeContext from "../../layout/themeContext";

export const WebphoneContext = createContext(null);

const defaultButtonConfig = {
  visible: true,
  position_y: "bottom",
  position_x: "right",
  spacing: "20px",
  color: "#00339E",
};

/**
 * Provider para o Webphone (WAVoIP): controla visibilidade do widget e
 * configuração do botão flutuante (FAB). A cor do botão e o tema do webphone
 * vêm do tema da aplicação (light/dark e cor primária).
 */
export const WebphoneProvider = ({ children, defaultConfig }) => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const [widgetVisible, setWidgetVisible] = useState(false);
  const [configOverrides, setConfigOverrides] = useState(defaultConfig?.button || {});

  const themeMode = theme?.palette?.type ?? theme?.palette?.mode ?? colorMode?.colorMode?.mode ?? "light";
  const primaryColor = theme?.palette?.primary?.main ?? defaultButtonConfig.color;

  const config = useMemo(
    () => ({
      button: {
        ...defaultButtonConfig,
        ...configOverrides,
        color: primaryColor,
      },
    }),
    [primaryColor, configOverrides]
  );

  const setConfig = (next) => {
    setConfigOverrides((prev) => {
      if (typeof next === "function") return next(prev);
      const nextButton = next?.button;
      if (!nextButton) return prev;
      return { ...prev, ...nextButton };
    });
  };

  useEffect(() => {
    if (typeof window === "undefined" || !window.wavoip?.theme?.setTheme) return;
    const mode = themeMode === "dark" ? "dark" : "light";
    window.wavoip.theme.setTheme(mode);
  }, [themeMode]);

  const toggleWidget = () => setWidgetVisible((v) => !v);
  const openWidget = () => setWidgetVisible(true);
  const closeWidget = () => setWidgetVisible(false);

  return (
    <WebphoneContext.Provider
      value={{
        widgetVisible,
        setWidgetVisible,
        toggleWidget,
        openWidget,
        closeWidget,
        config,
        setConfig,
        themeMode,
      }}
    >
      {children}
    </WebphoneContext.Provider>
  );
};

export const useWebphone = () => {
  const ctx = useContext(WebphoneContext);
  if (!ctx) {
    throw new Error("useWebphone must be used within WebphoneProvider");
  }
  return ctx;
};
